// Which kind of device and browser this is, so a hidden alert gets the right fix (Gary,
// 17 Oct: Chrome made every alert but the Mac never showed them). Pure, so it is tested
// (platform.test.ts); the app passes in navigator's values.

export type DeviceKind = 'mac' | 'windows' | 'iphone' | 'android' | 'other';
export type BrowserName = 'Chrome' | 'Edge' | 'Safari' | 'Firefox' | 'other';

export interface DeviceInfo {
  userAgent: string;
  platform?: string;
  maxTouchPoints?: number;
}

/** iPad on iPadOS 13+ reports itself as a Mac with a touch screen: it counts as iPhone here. */
export function deviceKind({ userAgent, platform = '', maxTouchPoints = 0 }: DeviceInfo): DeviceKind {
  if (/iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)) return 'iphone';
  if (/Android/i.test(userAgent)) return 'android';
  if (/Windows/i.test(userAgent) || /^Win/i.test(platform)) return 'windows';
  if (/Macintosh|Mac OS X/.test(userAgent) || /^Mac/.test(platform)) return 'mac';
  return 'other';
}

/** Edge and Chrome share most of their user agent: Edge says Edg/ as well. */
export function browserName(userAgent: string): BrowserName {
  if (/Edg(A|iOS)?\//.test(userAgent)) return 'Edge';
  if (/Firefox\/|FxiOS\//.test(userAgent)) return 'Firefox';
  if (/Chrome\/|CriOS\//.test(userAgent)) return 'Chrome';
  if (/Safari\//.test(userAgent)) return 'Safari';
  return 'other';
}

/**
 * The names the computer's notification settings list for this browser. On a Mac, Chrome
 * shows a second entry for its alerts (seen on Gary's Mac, 17 Oct).
 */
export function systemAppNames(kind: DeviceKind, browser: BrowserName): string[] {
  if (browser === 'Chrome') return kind === 'mac' ? ['Google Chrome', 'Google Chrome Helper (Alerts)'] : ['Google Chrome'];
  if (browser === 'Edge') return ['Microsoft Edge'];
  if (browser === 'Safari') return ['Safari'];
  if (browser === 'Firefox') return ['Firefox'];
  return ['your browser'];
}

export type AlertDiagnosis = 'ok' | 'blocked' | 'not_asked' | 'not_registered' | 'unsupported';

/**
 * What the app can know for sure about why a test alert was not seen: the browser's
 * permission and whether this device is registered for alerts. 'ok' means the browser side
 * is right, so the computer's or phone's own settings are hiding it.
 */
export function diagnose(permission: NotificationPermission | 'unsupported', registered: boolean | null): AlertDiagnosis {
  if (permission === 'unsupported') return 'unsupported';
  if (permission === 'denied') return 'blocked';
  if (permission === 'default') return 'not_asked';
  return registered === false ? 'not_registered' : 'ok';
}
